import { CasesApiService } from '@api_patient/cases_api.service';
import { Injectable } from '@visiba/instantiation';
import { Timer } from '@visiba/std';
import { MessagingCaseModel } from './case_messaging_model';
import { AppointmentCaseModel } from './case_appointment_model';
import { CaseCacheService } from './_case_cache.service';
import { DateService } from '@application/time/date.service';

export interface ListedData {
	appointmentCases: AppointmentCaseModel[];
	messagingCases: MessagingCaseModel[];
}
export type Disposer = () => void;
export type Callback = (data: ListedData) => void;

@Injectable()
export class CaseService {
	private readonly FETCH_INTERVAL = 60_000;
	private fetchTimer = new Timer();
	private isFetching = false;
	private hasTimer = false;

	private callbacks: Callback[] = [];

	constructor(
		private readonly casesApiService: CasesApiService,
		private readonly dateService: DateService,
		private readonly caseCache: CaseCacheService,
	) {
		this.caseCache.signaler.on('onCaseListUpdate', () => {
			this.updateSync();
		});
	}

	public pollData(callback: Callback): Disposer {
		const index = this.callbacks.length;
		this.callbacks.push(callback);

		this.startScheduledFetch();

		const listedData = this.caseCache.getListedData();
		if (listedData.appointmentCases.length > 0 || listedData.messagingCases.length > 0) {
			callback(listedData);
		}

		return () => {
			this.callbacks.splice(index, 1);

			if (this.callbacks.length === 0) {
				this.stopScheduledFetch();
			}
		};
	}

	public getGroupedCases(): {
		completed: {
			appointmentCases: AppointmentCaseModel[];
			messagingCases: MessagingCaseModel[];
		};
		ongoing: {
			appointmentCases: AppointmentCaseModel[];
			messagingCases: MessagingCaseModel[];
		};
	} {
		return this.caseCache.getGroupedCases();
	}

	public updateSync(): void {
		const listedData = this.caseCache.getListedData();

		for (const callback of this.callbacks) {
			callback(listedData);
		}
	}

	private startScheduledFetch(): void {
		if (this.hasTimer) return;

		this.hasTimer = true;
		this.fetchTimer.repeat(() => this.fetch(), this.FETCH_INTERVAL, true);
	}

	private stopScheduledFetch(): void {
		this.hasTimer = false;
		this.isFetching = false;
		this.fetchTimer.flush();
	}

	private async fetch(): Promise<void> {
		if (this.isFetching) return;

		this.isFetching = true;
		const result = await this.casesApiService.get();
		// TODO: handle error
		if (result.err) return;

		const data = result.ok.data;
		this.isFetching = false;

		for (const caseData of data) {
			switch (caseData.Type) {
				case 'Appointment':
				case 'PhysicalAppointment': {
					this.caseCache.updateAppointmentCase(caseData);

					break;
				}
				case 'MessagingConversation': {
					this.caseCache.updateMessagingCase(caseData);

					break;
				}
			}
		}

		this.updateSync();
	}
}
